/**
 * CharacterFormatManager - MS Word-style character-level formatting
 * 
 * Manages text formatting at the character level, supporting:
 * - Font family, size, color changes
 * - Bold, italic, underline styling
 * - Background highlighting
 * - Format persistence and serialization
 * 
 * Integration with EdgeDocs:
 * - Works with existing SelectionManager for range-based formatting
 * - Integrates with SyncManager for server synchronization
 * - Persists formatting in AST metadata for database storage
 */

import { LocalDocumentEngine } from '../core/LocalDocumentEngine';
import { SelectionRange } from '../types/DocumentTypes';

export interface CharacterFormat {
  // Font properties
  fontFamily?: string;
  fontSize?: number;        // in points
  fontColor?: string;       // hex color
  backgroundColor?: string; // hex color for highlighting
  
  // Style properties
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  
  // Paragraph properties (applied to entire paragraphs)
  alignment?: 'left' | 'center' | 'right' | 'justify';
}

export interface FormatRange {
  start: number;
  end: number;
  format: CharacterFormat;
}

export interface FormattedDocument {
  text: string;
  formats: FormatRange[];
}

export class CharacterFormatManager {
  private documentEngine: LocalDocumentEngine;
  private formatRanges: FormatRange[] = [];
  
  // Event listeners
  private listeners: Map<string, Function[]> = new Map();
  
  constructor(documentEngine: LocalDocumentEngine) {
    this.documentEngine = documentEngine;
    
    // Listen for text changes to update format ranges
    this.documentEngine.on('textChanged', ({ text, version }: { text: string; version: number }) => {
      this.validateFormatRanges(text.length);
    });
  }

  // ============================================================================
  // FORMAT APPLICATION
  // ============================================================================

  /**
   * Apply formatting to selected text range
   */
  applyFormat(selection: SelectionRange, format: Partial<CharacterFormat>): void {
    if (selection.start === selection.end) {
      console.warn('[CharacterFormatManager] Cannot apply format to empty selection');
      return;
    }

    console.log(`[CharacterFormatManager] Applying format to range ${selection.start}-${selection.end}:`, format);

    // Remove existing formats in this range
    this.removeFormatInRange(selection.start, selection.end);
    
    // Split existing ranges that overlap with selection
    this.splitOverlappingRanges(selection.start, selection.end);
    
    // Apply new format
    const newFormatRange: FormatRange = {
      start: selection.start,
      end: selection.end,
      format: { ...format }
    };
    
    this.formatRanges.push(newFormatRange);
    
    // Merge adjacent ranges with identical formatting
    this.mergeAdjacentRanges();
    
    // Sort ranges by start position
    this.formatRanges.sort((a, b) => a.start - b.start);
    
    // Emit format change event
    this.emit('formatChanged', {
      range: selection,
      format,
      allFormats: this.formatRanges
    });
    
    console.log(`[CharacterFormatManager] Format applied. Total ranges: ${this.formatRanges.length}`);
  }

  /**
   * Get formatting at specific position
   */
  getFormatAtPosition(position: number): CharacterFormat {
    for (const range of this.formatRanges) {
      if (position >= range.start && position < range.end) {
        return { ...range.format };
      }
    }
    
    // Return default format if no specific formatting found
    return this.getDefaultFormat();
  }

  /**
   * Get all formats in a range
   */
  getFormatsInRange(start: number, end: number): FormatRange[] {
    return this.formatRanges.filter(range => 
      range.start < end && range.end > start
    );
  }

  /**
   * Clear all formatting in range
   */
  clearFormatInRange(start: number, end: number): void {
    console.log(`[CharacterFormatManager] Clearing format in range ${start}-${end}`);
    
    this.removeFormatInRange(start, end);
    
    this.emit('formatChanged', {
      range: { start, end, direction: 'forward' },
      format: this.getDefaultFormat(),
      allFormats: this.formatRanges
    });
  }

  // ============================================================================
  // FORMAT PERSISTENCE
  // ============================================================================

  /**
   * Serialize formatting data for database storage
   */
  serializeFormats(): any {
    return {
      version: '1.0',
      formatRanges: this.formatRanges.map(range => ({
        start: range.start,
        end: range.end,
        format: { ...range.format }
      })),
      createdAt: new Date().toISOString()
    };
  }

  /**
   * Load formatting data from database
   */
  loadFormats(serializedData: any): void {
    if (!serializedData || !serializedData.formatRanges) {
      console.log('[CharacterFormatManager] No formatting data to load');
      return;
    }

    console.log(`[CharacterFormatManager] Loading ${serializedData.formatRanges.length} format ranges`);
    
    this.formatRanges = serializedData.formatRanges.map((range: any) => ({
      start: range.start,
      end: range.end,
      format: { ...range.format }
    }));
    
    // Validate loaded ranges
    const textLength = this.documentEngine.getText().length;
    this.validateFormatRanges(textLength);
    
    this.emit('formatChanged', {
      range: null,
      format: null,
      allFormats: this.formatRanges
    });
  }

  /**
   * Export formatted document for rendering
   */
  exportFormattedDocument(): FormattedDocument {
    return {
      text: this.documentEngine.getText(),
      formats: [...this.formatRanges]
    };
  }

  // ============================================================================
  // INTERNAL UTILITIES
  // ============================================================================

  private removeFormatInRange(start: number, end: number): void {
    this.formatRanges = this.formatRanges.filter(range => {
      // Remove ranges completely within the target range
      if (range.start >= start && range.end <= end) {
        return false;
      }
      return true;
    });
  }

  private splitOverlappingRanges(start: number, end: number): void {
    const newRanges: FormatRange[] = [];
    
    for (const range of this.formatRanges) {
      if (range.start < start && range.end > start && range.end <= end) {
        // Range starts before and ends within target
        newRanges.push({
          start: range.start,
          end: start,
          format: { ...range.format }
        });
      } else if (range.start >= start && range.start < end && range.end > end) {
        // Range starts within and ends after target
        newRanges.push({
          start: end,
          end: range.end,
          format: { ...range.format }
        });
      } else if (range.start < start && range.end > end) {
        // Range completely encompasses target - split into two
        newRanges.push({
          start: range.start,
          end: start,
          format: { ...range.format }
        });
        newRanges.push({
          start: end,
          end: range.end,
          format: { ...range.format }
        });
      } else if (range.end <= start || range.start >= end) {
        // Range doesn't overlap with target
        newRanges.push(range);
      }
      // Ranges completely within target are removed (handled by removeFormatInRange)
    }
    
    this.formatRanges = newRanges;
  }

  private mergeAdjacentRanges(): void {
    if (this.formatRanges.length <= 1) return;
    
    const merged: FormatRange[] = [];
    let current = this.formatRanges[0];
    
    for (let i = 1; i < this.formatRanges.length; i++) {
      const next = this.formatRanges[i];
      
      if (current.end === next.start && this.formatsEqual(current.format, next.format)) {
        // Merge adjacent ranges with identical formatting
        current = {
          start: current.start,
          end: next.end,
          format: { ...current.format }
        };
      } else {
        merged.push(current);
        current = next;
      }
    }
    
    merged.push(current);
    this.formatRanges = merged;
  }

  private formatsEqual(format1: CharacterFormat, format2: CharacterFormat): boolean {
    const keys = new Set([...Object.keys(format1), ...Object.keys(format2)]);
    
    for (const key of keys) {
      if ((format1 as any)[key] !== (format2 as any)[key]) {
        return false;
      }
    }
    
    return true;
  }

  private validateFormatRanges(textLength: number): void {
    // Remove invalid ranges and clip ranges that extend beyond text
    this.formatRanges = this.formatRanges
      .filter(range => range.start < textLength && range.end > 0)
      .map(range => ({
        ...range,
        start: Math.max(0, range.start),
        end: Math.min(textLength, range.end)
      }))
      .filter(range => range.start < range.end);
  }

  private getDefaultFormat(): CharacterFormat {
    return {
      fontFamily: 'Times New Roman',
      fontSize: 12,
      fontColor: '#000000',
      backgroundColor: 'transparent',
      bold: false,
      italic: false,
      underline: false,
      alignment: 'left'
    };
  }

  // ============================================================================
  // EVENT SYSTEM
  // ============================================================================

  on(event: string, callback: Function): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(callback);
  }

  private emit(event: string, data: any): void {
    const callbacks = this.listeners.get(event) || [];
    callbacks.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`[CharacterFormatManager] Error in ${event} callback:`, error);
      }
    });
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Get all format ranges (for debugging)
   */
  getAllFormatRanges(): FormatRange[] {
    return [...this.formatRanges];
  }

  /**
   * Clear all formatting
   */
  clearAllFormats(): void {
    this.formatRanges = [];
    this.emit('formatChanged', {
      range: null,
      format: null,
      allFormats: []
    });
  }
}
